import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiHome } from 'react-icons/fi';

import { apiService } from '../../components/Config/api.service';
import { canGererDeparts } from '../../components/Auth/permissions';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { lireEnBase64 } from '../../components/Utils/lireEnBase64';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE, enveloppeDe } from './organigrammeOutils';

/**
 * Les domiciliations de salaire d'un salarié, sur sa fiche (contrat C35,
 * Yros/Kavero#435, #62) : la banque, son adresse e-mail, la date et
 * l'attestation. La RH en ajoute, corrige l'adresse d'une domiciliation
 * active, la lève, et télécharge la pièce. Au départ du salarié, la banque se
 * prévient depuis la fiche du départ.
 *
 * Réservé à la RH avec l'offre RH — confort d'interface, la barrière est côté
 * Odoo. La pièce part en base64 ; 5 Mo au plus, comme le proxy l'exige.
 */
const PIECE_MAX_OCTETS = 5 * 1024 * 1024;
const CLASSE_CHAMP = 'mt-1 w-full px-3 py-2 border rounded-lg text-gray-700 bg-gray-50 text-sm';
const VIDE = { banque: '', email: '', date: '', fichier: null };

function Domiciliations({ employe }) {
  const { t } = useTranslation();
  const visible = canGererDeparts();
  const [domiciliations, setDomiciliations] = useState([]);
  const [erreurLecture, setErreurLecture] = useState(false);
  const [formulaire, setFormulaire] = useState(null);
  const [edition, setEdition] = useState(null); // { id, email }
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!visible || !employe.id) return undefined;
    let actif = true;
    enveloppeDe(() => apiService.listerDomiciliations(employe.id)).then((reponse) => {
      if (!actif) return;
      setDomiciliations(reponse.ok ? reponse.domiciliations || [] : []);
      setErreurLecture(!reponse.ok);
    });
    return () => { actif = false; };
  }, [visible, employe.id]);

  if (!visible) return null;

  const remplacer = (domiciliation) => setDomiciliations((courantes) =>
    courantes.map((d) => (d.id === domiciliation.id ? domiciliation : d)));

  const ajouter = async (event) => {
    event.preventDefault();
    const { banque, email, date, fichier } = formulaire;
    if (!banque.trim() || !email.trim()) {
      setErreur(t('domiciliations.champsManquants'));
      return;
    }
    if (fichier && fichier.size > PIECE_MAX_OCTETS) {
      setErreur(t('domiciliations.pieceTropLourde'));
      return;
    }
    const valeurs = { banque: banque.trim(), email_banque: email.trim() };
    if (date) valeurs.date_domiciliation = date;
    setEnCours(true);
    setErreur('');
    try {
      if (fichier) {
        valeurs.piece = await lireEnBase64(fichier);
        valeurs.piece_nom = fichier.name;
      }
    } catch (e) {
      signalerErreur(e, 'Domiciliations.lirePiece');
      setEnCours(false);
      setErreur(t('domiciliations.pieceIllisible'));
      return;
    }
    const reponse = await enveloppeDe(() => apiService.creerDomiciliation(employe.id, valeurs));
    setEnCours(false);
    if (reponse.ok) {
      setDomiciliations((courantes) => [reponse.domiciliation, ...courantes]);
      setFormulaire(null);
    } else {
      setErreur(reponse.message || t('domiciliations.erreurGenerique'));
    }
  };

  const appeler = async (appel, apres) => {
    setEnCours(true);
    setErreur('');
    const reponse = await enveloppeDe(appel);
    setEnCours(false);
    if (reponse.ok) {
      remplacer(reponse.domiciliation);
      if (apres) apres();
    } else {
      setErreur(reponse.message || t('domiciliations.erreurGenerique'));
    }
  };

  const telecharger = async (domiciliation) => {
    try {
      await apiService.telechargerPieceDomiciliation(domiciliation);
    } catch (e) {
      signalerErreur(e, 'Domiciliations.telecharger');
      setErreur(t('domiciliations.erreurTelechargement'));
    }
  };

  return (
    <div className="py-6 px-3" data-testid="fiche-domiciliations">
      <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <FiHome className="text-[--color-primary]" /> {t('domiciliations.titre')}
      </h3>
      {erreurLecture && <p className="text-sm text-gray-500">{t('domiciliations.erreurLecture')}</p>}
      {!erreurLecture && domiciliations.length === 0 && !formulaire && (
        <p className="text-sm text-gray-500">{t('domiciliations.aucune')}</p>
      )}

      <ul className="space-y-3 text-sm">
        {domiciliations.map((d) => (
          <li key={d.id} className="rounded-lg border border-slate-200 p-3" data-testid={`domiciliation-${d.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium">{d.banque}</p>
                {edition?.id === d.id ? (
                  <form className="flex gap-2 mt-1" onSubmit={(e) => {
                    e.preventDefault();
                    appeler(() => apiService.modifierDomiciliation(d.id, { email_banque: edition.email.trim() }),
                      () => setEdition(null));
                  }}>
                    <input type="email" data-testid={`domiciliation-email-${d.id}`} value={edition.email}
                      onChange={(e) => setEdition({ ...edition, email: e.target.value })} className={CLASSE_CHAMP} />
                    <button type="submit" disabled={enCours} className={BOUTON_PRINCIPAL}>{t('domiciliations.enregistrer')}</button>
                    <button type="button" className={BOUTON_SECONDAIRE} onClick={() => setEdition(null)}>{t('domiciliations.annuler')}</button>
                  </form>
                ) : (
                  <p className="text-gray-500">{d.email_banque}</p>
                )}
                <p className="text-gray-500">
                  {d.date_domiciliation ? t('domiciliations.depuis', { date: d.date_domiciliation }) : ''}
                  {d.etat === 'levee' && (
                    <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                      {t('domiciliations.levee')}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {d.piece && (
                  <button type="button" data-testid={`domiciliation-piece-${d.id}`} className={BOUTON_SECONDAIRE}
                    onClick={() => telecharger(d)}>
                    <span className="inline-flex items-center gap-1"><FiDownload /> {t('domiciliations.piece')}</span>
                  </button>
                )}
                {d.etat === 'active' && edition?.id !== d.id && (
                  <>
                    <button type="button" data-testid={`domiciliation-modifier-${d.id}`} className={BOUTON_SECONDAIRE}
                      disabled={enCours} onClick={() => setEdition({ id: d.id, email: d.email_banque })}>
                      {t('domiciliations.modifier')}
                    </button>
                    <button type="button" data-testid={`domiciliation-lever-${d.id}`} disabled={enCours}
                      className="px-3 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                      onClick={() => window.confirm(t('domiciliations.confirmerLevee', { banque: d.banque }))
                        && appeler(() => apiService.leverDomiciliation(d.id))}>
                      {t('domiciliations.lever')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {formulaire ? (
        <form onSubmit={ajouter} data-testid="domiciliation-formulaire" className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm font-medium">
            {t('domiciliations.banque')}
            <input type="text" data-testid="domiciliation-banque" value={formulaire.banque} className={CLASSE_CHAMP}
              onChange={(e) => setFormulaire({ ...formulaire, banque: e.target.value })} />
          </label>
          <label className="block text-sm font-medium">
            {t('domiciliations.email')}
            <input type="email" data-testid="domiciliation-email" value={formulaire.email} className={CLASSE_CHAMP}
              onChange={(e) => setFormulaire({ ...formulaire, email: e.target.value })} />
          </label>
          <label className="block text-sm font-medium">
            {t('domiciliations.date')}
            <input type="date" data-testid="domiciliation-date" value={formulaire.date} className={CLASSE_CHAMP}
              onChange={(e) => setFormulaire({ ...formulaire, date: e.target.value })} />
          </label>
          <label className="block text-sm font-medium">
            {t('domiciliations.attestation')}
            <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="domiciliation-fichier"
              className="mt-1 block text-sm"
              onChange={(e) => setFormulaire({ ...formulaire, fichier: e.target.files?.[0] || null })} />
          </label>
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" className={BOUTON_SECONDAIRE} onClick={() => { setFormulaire(null); setErreur(''); }}>
              {t('domiciliations.annuler')}
            </button>
            <button type="submit" data-testid="domiciliation-ajouter" disabled={enCours} className={BOUTON_PRINCIPAL}>
              {t('domiciliations.ajouter')}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" data-testid="domiciliation-nouvelle" className={`${BOUTON_SECONDAIRE} mt-3`}
          onClick={() => setFormulaire(VIDE)}>
          {t('domiciliations.nouvelle')}
        </button>
      )}
      {erreur && (
        <p role="alert" className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{erreur}</p>
      )}
    </div>
  );
}

export default Domiciliations;
